/**
 * Baixar a lista de contatos do grupo — o seguro contra o WhatsApp banir o grupo.
 *
 * O pedido do dono, com as palavras dele: *"caso algum grupo seja banido, conseguimos
 * exportar a lista de contatos e mandar mensagens posteriormente para entrada em um novo
 * grupo, seria como uma segurança para não perder os leads"*. Hoje um banimento perde todos
 * os leads de uma vez, sem recurso.
 *
 * Quatro coisas que este card faz de propósito:
 *
 *  1. **Diz o tamanho do seguro ANTES do clique.** Um arquivo com 550 linhas e 12 telefones
 *     não salva ninguém, e descobrir isso no dia do banimento é tarde demais.
 *  2. **Grupo nunca varrido não ganha botão de baixar** — ganha o motivo e o caminho para
 *     resolver. O servidor recusa de qualquer jeito; a tela é quem evita a viagem perdida.
 *  3. **Fala do dado pessoal sem teatro.** Uma frase: o que sai, de quantas pessoas, e que
 *     a partir do download o arquivo é responsabilidade de quem baixou. Nada de "aceito os
 *     termos" que ninguém lê.
 *  4. **O download é um link, não um `fetch`.** O navegador salva o arquivo com o nome que
 *     o servidor mandou, e o painel nunca segura 1218 contatos na memória da aba.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  SkeletonText,
  buttonVariants,
  cn,
} from '@mgdlead/ui';
import { Download, LifeBuoy, ShieldAlert } from 'lucide-react';

import { FormAlert } from '@mgdlead/ui';
import { contactsExportUrl, useContactsExportPreview } from '../../../lib/api';
import { formatExact } from '../../../lib/datetime';

export interface ExportContactsCardProps {
  workspaceId: string;
  groupId: string;
  timezone: string;
  /** `can(role, 'group:export_contacts')`. Esconder é UX; quem barra é o servidor. */
  allowed: boolean;
  /** Oferecido quando o grupo nunca foi varrido: sem isso o arquivo sairia quase vazio. */
  onSyncPeople?: (() => void) | undefined;
  syncing?: boolean;
}

export function ExportContactsCard({
  workspaceId,
  groupId,
  timezone,
  allowed,
  onSyncPeople,
  syncing = false,
}: ExportContactsCardProps) {
  const [open, setOpen] = useState(false);
  const preview = useContactsExportPreview(workspaceId, groupId, allowed);

  /**
   * Quem não tem a permissão não vê o card inteiro — nem o número de telefones.
   * `group:export_contacts` é separada de `group:read` justamente porque ver a tela e levar
   * a base de telefones para fora são riscos diferentes.
   */
  if (!allowed) return null;

  const dados = preview.data;

  return (
    <Card className="mt-3">
      <CardHeader>
        <CardTitle>
          <LifeBuoy aria-hidden="true" className="mr-1.5 inline size-4 align-[-0.1875rem]" />
          Se este grupo cair
        </CardTitle>
        <CardDescription>
          Grupo banido pelo WhatsApp leva todos os leads de uma vez, e não tem recurso. Com a lista
          de contatos salva, dá para chamar essas pessoas para um grupo novo.
        </CardDescription>
      </CardHeader>

      <div className="mt-3 flex flex-col gap-3">
        {preview.isPending ? (
          <SkeletonText lines={2} />
        ) : preview.isError || dados === undefined || dados === null ? (
          <FormAlert tone="warning">
            Não conseguimos conferir o que sairia no arquivo agora. Tente de novo em instantes.
          </FormAlert>
        ) : !dados.ready ? (
          /*
            O detalhe que separa "arquivo bonito" de "seguro que funciona": sem a varredura,
            `group_participants` conhece só quem passou por um evento nosso — num grupo de
            550 pessoas isso são duas ou três linhas. Deixar baixar assim é entregar um
            seguro vazio que só será aberto no dia em que não dá mais para consertar.
          */
          <>
            <FormAlert tone="warning">
              Este grupo ainda não passou por uma sincronização de pessoas: o painel sabe quantas
              são, não quem são. Baixar agora traria um arquivo quase vazio — e isso só apareceria
              no dia em que ele fizesse falta.
            </FormAlert>
            {onSyncPeople ? (
              <Button
                variant="secondary"
                block
                loading={syncing}
                loadingLabel="Sincronizando"
                onClick={onSyncPeople}
              >
                Sincronizar pessoas primeiro
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1">
              <Badge tone="outline" numeric>
                {dados.total} {dados.total === 1 ? 'pessoa' : 'pessoas'}
              </Badge>
              {/*
                O número que diz se o seguro paga. Cor não é canal único: o texto está escrito
                do lado, e o tom só reforça.
              */}
              <Badge
                tone={dados.withPhone === dados.total ? 'success' : 'warning'}
                dot
                numeric
                title="Sem telefone não dá para chamar a pessoa de volta. O telefone vem da sincronização de pessoas, e nem todo participante o entrega."
              >
                {dados.withPhone} com telefone
              </Badge>
            </div>

            <p className="text-2xs text-muted-foreground">
              Lista conferida pela última vez em{' '}
              {formatExact(dados.participantsSyncedAt, timezone) ?? 'nunca'}. Ela só fica em dia se
              você sincronizar as pessoas de vez em quando.
            </p>

            {/*
              `secondary`, não `primary`: a tela já tem um CTA âmbar — "Sincronizar pessoas",
              no cabeçalho — e o DESIGN_SYSTEM (§1.5) manda uma cor de ação só por tela. O
              destaque desta aqui vem da largura cheia e do ícone, não de disputar a cor. A
              confirmação, dentro do diálogo, aí sim é primária: ali ela é a única ação.
            */}
            <Button
              variant="secondary"
              leadingIcon={<Download />}
              block
              disabled={dados.total === 0}
              onClick={() => setOpen(true)}
            >
              Baixar lista de contatos
            </Button>

            {dados.total === 0 ? (
              <p className="text-2xs text-muted-foreground">
                A sincronização não encontrou ninguém neste grupo, então não há o que salvar.
              </p>
            ) : null}
          </>
        )}
      </div>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Baixar a lista de contatos"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            {/*
              Link, e não botão com `fetch`: o navegador salva o arquivo com o nome que o
              servidor mandou (`Content-Disposition`) e a aba nunca segura 1218 contatos na
              memória. O cookie de sessão viaja porque a URL é da mesma origem.
            */}
            <a
              href={contactsExportUrl(workspaceId, groupId)}
              download
              className={cn(buttonVariants({ variant: 'primary' }), 'gap-1.5')}
              onClick={() => setOpen(false)}
            >
              <Download aria-hidden="true" className="size-4" />
              Baixar agora
            </a>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm text-muted-foreground">
          <p>
            <ShieldAlert
              aria-hidden="true"
              className="mr-1 inline size-4 align-[-0.1875rem] text-warning"
            />
            O arquivo leva <strong className="text-foreground">telefone em claro</strong> de{' '}
            <strong className="text-foreground nums">{dados?.withPhone ?? 0}</strong>{' '}
            {(dados?.withPhone ?? 0) === 1 ? 'pessoa' : 'pessoas'}
            {dados !== undefined && dados.total !== dados.withPhone ? (
              <> (das {dados.total} da lista; o resto não tem telefone conhecido)</>
            ) : null}
            .
          </p>
          {/*
            LGPD sem teatro: o fato, visível, para quem clica. Não há consentimento a pedir
            aqui — o que existe é uma responsabilidade que muda de mãos no momento do
            download, e dizer isso em uma frase vale mais que um parágrafo de termos.
          */}
          <p>
            Dentro do painel esses números ficam guardados de forma pseudonimizada. Fora dele, não:
            a partir do download o arquivo é um dado pessoal sob a sua guarda — quem o copiar, para
            onde ele for e por quanto tempo ele existir passam a ser decisão sua.
          </p>
          <p>Esta exportação fica registrada: quem baixou, quando e quantas linhas saíram.</p>
        </div>
      </Dialog>
    </Card>
  );
}
